import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Grid2,
  Paper,
  FormControl,
  Select,
  MenuItem,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Chip,
  useTheme,
  CircularProgress,
  Alert,
  InputLabel,
} from '@mui/material';
import {
  TrendingUp,
  Visibility,
  Chat,
  AccessTime,
  People,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import axios from '../../../api/myAxios';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';

// Enregistrement des composants Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

interface AnalyticsProps {
  selectedWorkspace: any;
}

const Analytics: React.FC<AnalyticsProps> = ({ selectedWorkspace }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [period, setPeriod] = useState('30d');

  // Fetch dashboard data
  const {
    data: dashboardData,
    isLoading: isDashboardLoading,
    error: dashboardError,
  } = useQuery({
    queryKey: ['analytics-dashboard', selectedWorkspace?.id, period],
    queryFn: async () => {
      const response = await axios.get(
        `/api/analytics/dashboard/${selectedWorkspace.id}?period=${period}`
      );
      return response.data;
    },
    enabled: !!selectedWorkspace?.id,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Fetch visitors data
  const { data: visitorsData, isLoading: isVisitorsLoading } = useQuery({
    queryKey: ['analytics-visitors', selectedWorkspace?.id, period],
    queryFn: async () => {
      const response = await axios.get(
        `/api/analytics/visitors/${selectedWorkspace.id}?period=${period}`
      );
      return response.data;
    },
    enabled: !!selectedWorkspace?.id,
    staleTime: 1000 * 60 * 5,
  });

  // Fetch popular questions
  const { data: questionsData, isLoading: isQuestionsLoading } = useQuery({
    queryKey: ['analytics-questions', selectedWorkspace?.id, period],
    queryFn: async () => {
      const response = await axios.get(
        `/api/analytics/popular-questions/${selectedWorkspace.id}?period=${period}`
      );
      return response.data;
    },
    enabled: !!selectedWorkspace?.id,
    staleTime: 1000 * 60 * 5,
  });

  if (!selectedWorkspace) {
    return (
      <Box sx={{ width: '100%' }}>
        <Alert severity="info" sx={{ mt: 4 }}>
          {t('analytics.selectWorkspace')}
        </Alert>
      </Box>
    );
  }

  if (isDashboardLoading) {
    return (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '200px',
        }}
      >
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>
          {t('analytics.loadingAnalytics')}
        </Typography>
      </Box>
    );
  }

  if (dashboardError) {
    return (
      <Box sx={{ width: '100%' }}>
        <Alert severity="error" sx={{ mt: 4 }}>
          {t('analytics.errorLoadingAnalytics')}
        </Alert>
      </Box>
    );
  }

  // Configuration des graphiques
  const trafficChartData = {
    labels:
      dashboardData?.trafficByDay?.map((day: any) =>
        new Date(day.date).toLocaleDateString('fr-FR', {
          month: 'short',
          day: 'numeric',
        })
      ) || [],
    datasets: [
      {
        label: t('analytics.pageViews', 'Vues de page'),
        data: dashboardData?.trafficByDay?.map((day: any) => day.views) || [],
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.primary.main + '20',
        tension: 0.4,
      },
      {
        label: t('analytics.uniqueVisitors', 'Visiteurs uniques'),
        data:
          dashboardData?.trafficByDay?.map((day: any) => day.uniqueVisitors) ||
          [],
        borderColor: theme.palette.secondary.main,
        backgroundColor: theme.palette.secondary.main + '20',
        tension: 0.4,
      },
    ],
  };

  const deviceChartData = {
    labels: visitorsData?.deviceTypes?.map((device: any) => device.type) || [],
    datasets: [
      {
        data:
          visitorsData?.deviceTypes?.map((device: any) => device.count) || [],
        backgroundColor: [
          theme.palette.primary.main,
          theme.palette.secondary.main,
          theme.palette.success.main,
        ],
      },
    ],
  };

  const hourlyChartData = {
    labels: Array.from({ length: 24 }, (_, i) => `${i}h`),
    datasets: [
      {
        label: t('analytics.visitsPerHour', 'Visites par heure'),
        data: Array.from({ length: 24 }, (_, hour) => {
          const hourData = visitorsData?.visitorsByHour?.find(
            (h: any) => h.hour === hour
          );
          return hourData?.count || 0;
        }),
        backgroundColor: theme.palette.primary.main + '60',
        borderColor: theme.palette.primary.main,
        borderWidth: 1,
      },
    ],
  };

  return (
    <Box sx={{ width: '100%', p: 0 }}>
      <Box sx={{ mb: 4 }}>
        <Box
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          mb={3}
        >
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>{t('analytics.timeRange')}</InputLabel>
            <Select
              value={period}
              label={t('analytics.timeRange')}
              onChange={(e) => setPeriod(e.target.value)}
            >
              <MenuItem value="7d">{t('analytics.last7Days')}</MenuItem>
              <MenuItem value="30d">{t('analytics.last30Days')}</MenuItem>
              <MenuItem value="90d">{t('analytics.last90Days')}</MenuItem>
              <MenuItem value="365d">{t('analytics.allTime')}</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Métriques principales */}
        <Grid2 container spacing={3} sx={{ mb: 4 }}>
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Visibility color="primary" />
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {dashboardData?.overview?.totalViews || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('analytics.pageViews')}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <People color="secondary" />
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {dashboardData?.overview?.uniqueVisitors || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('analytics.uniqueVisitors')}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Chat color="success" />
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {dashboardData?.overview?.totalMessages || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('analytics.chatMessages')}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <AccessTime color="warning" />
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {Math.round(
                        dashboardData?.overview?.avgSessionDuration || 0
                      ) > 60
                        ? Math.round(
                            (dashboardData?.overview?.avgSessionDuration || 0) /
                              60
                          ) + 'm'
                        : Math.round(
                            dashboardData?.overview?.avgSessionDuration || 0
                          ) + 's'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('analytics.averageTime')}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid2>
        </Grid2>

        {/* Graphiques */}
        <Grid2 container spacing={3}>
          {/* Graphique du trafic */}
          <Grid2 size={{ xs: 12, md: 8 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t('analytics.trafficOverTime')}
              </Typography>
              <Box sx={{ height: 400 }}>
                <Line
                  data={trafficChartData}
                  options={{ responsive: true, maintainAspectRatio: false }}
                />
              </Box>
            </Paper>
          </Grid2>

          {/* Types d'appareils */}
          <Grid2 size={{ xs: 12, md: 4 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t('analytics.deviceTypes')}
              </Typography>
              <Box sx={{ height: 300 }}>
                {!isVisitorsLoading && visitorsData?.deviceTypes?.length > 0 ? (
                  <Doughnut
                    data={deviceChartData}
                    options={{ responsive: true, maintainAspectRatio: false }}
                  />
                ) : (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="100%"
                  >
                    <Typography variant="body2" color="text.secondary">
                      {t('analytics.noData')}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid2>

          {/* Visites par heure */}
          <Grid2 size={{ xs: 12, md: 8 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t('analytics.visitsPerHour')}
              </Typography>
              <Box sx={{ height: 300 }}>
                {!isVisitorsLoading ? (
                  <Bar
                    data={hourlyChartData}
                    options={{ responsive: true, maintainAspectRatio: false }}
                  />
                ) : (
                  <CircularProgress />
                )}
              </Box>
            </Paper>
          </Grid2>

          {/* Questions populaires */}
          <Grid2 size={{ xs: 12, md: 4 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t('analytics.topQuestions')}
              </Typography>
              {!isQuestionsLoading && questionsData?.questions?.length > 0 ? (
                <List>
                  {questionsData.questions
                    .slice(0, 5)
                    .map((question: any, index: number) => (
                      <ListItem key={index} divider>
                        <Box sx={{ width: '100%' }}>
                          <Typography variant="body2" noWrap sx={{ mb: 1 }}>
                            {question.question}
                          </Typography>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Chip
                              label={`${question.count} fois`}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                            {question.avgResponseTime > 0 && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {question.avgResponseTime}ms
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </ListItem>
                    ))}
                </List>
              ) : (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height="200px"
                >
                  {isQuestionsLoading ? (
                    <CircularProgress />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {t('analytics.noQuestionsYet')}
                    </Typography>
                  )}
                </Box>
              )}
            </Paper>
          </Grid2>
        </Grid2>
      </Box>
    </Box>
  );
};

export default Analytics;
